import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
} from "@mui/material";
import { motion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.css";
import WorkProcessComponent from "../home/HowWeWorks";
import SeoInfo from "../../SeoInfo";

const AboutPage = () => {
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
  };

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <SeoInfo
        title="Expert Car Mechanics | Most Trusted Garages | Go Clutch"
        description="Most trusted car Service Garages & Mechanics for all repairs & maintenance. From engine issues to quick fixes, we keep your car running smoothly—Book now!"
      />
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              textAlign: "left",
              color: "orangeRed",
              position: "relative",
              "&:after": {
                content: '""',
                position: "absolute",
                bottom: "-10px",
                left: 0,
                width: "60px",
                height: "4px",
                background: "orange",
              },
            }}
          >
            About goClutch
          </Typography>
        </motion.div>
        <Typography variant="body1" sx={{ mt: 3, color: "black" }}>
          At goClutch, we believe that your car deserves the very best
          service—whether it’s for a routine maintenance check or a detailed
          paint job. Our journey began in 2019 with a mission to redefine the
          way car services are delivered. We’ve grown exponentially since then,
          serving thousands of happy customers who trust us with their vehicles.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, color: "black" }}>
          Our services are tailored to meet the needs of modern car owners,
          providing everything from periodic maintenance to full car detailing.
          What sets us apart is our dedication to quality, convenience, and
          customer satisfaction. We make sure that every service is a seamless
          experience—from booking to delivery—because at goClutch, your car is
          in expert hands.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, color: "black" }}>
          Our professional team ensures that every vehicle is treated with the
          utmost care, using the latest tools and techniques to bring out the
          best in your car. With goClutch, you're not just getting a service;
          you're getting peace of mind.
        </Typography>
      </Box>
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h3"
          sx={{ textAlign: "center", fontWeight: "bold", color: "orangeRed" }}
        >
          Our Values
        </Typography>
        <Grid container spacing={4} sx={{ mt: 5 }}>
          {[
            {
              title: "Trust",
              icon: "🛡️",
              description: "We build long-term relationships based on trust.",
            },
            {
              title: "Innovation",
              icon: "💡",
              description:
                "We embrace the latest in automotive service technology.",
            },
            {
              title: "Customer Satisfaction",
              icon: "😊",
              description:
                "Our customers’ happiness is at the heart of everything we do.",
            },
          ].map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3 },
                }}
              >
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #f9f9f9, #ffffff)",
                    padding: "30px",
                    textAlign: "center",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, orange, orangeRed)",
                      color: "white",
                    },
                  }}
                >
                  <Typography variant="h2" sx={{ fontSize: "3rem" }}>
                    {value.icon}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      mt: 2,
                      color: "orangeRed",
                      "&:hover": { color: "white" },
                    }}
                  >
                    {value.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, color: "black" }}>
                    {value.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      <WorkProcessComponent />
      {/* Company Timeline */}
      <Box sx={{ mb: 8, position: "relative", width: "100%" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              color: "orangeRed",
              textAlign: "center",
              position: "relative",
              "&:after": {
                content: '""',
                position: "absolute",
                bottom: "-10px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "80px",
                height: "4px",
                background: "orange",
              },
            }}
          >
            Our Journey
          </Typography>
        </motion.div>

        {/* Timeline Path */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 6,
          }}
        >
          {/* Jointed Progress Path */}
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "80%" }} // Animate the width to 80% to symbolize progress
            transition={{ duration: 1 }}
            sx={{
              position: "absolute",
              top: "40px", // Adjust to align with the center of the milestones
              height: "4px",
              background: "orangeRed",
              zIndex: 1,
            }}
          />

          {/* Dots representing progress */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                background: "orangeRed",
                borderRadius: "50%",
                zIndex: 2,
              }}
            ></Box>
            <Box
              sx={{
                width: "20px",
                height: "20px",
                background: "orangeRed",
                borderRadius: "50%",
                zIndex: 2,
              }}
            ></Box>
          </Box>
        </Box>

        {/* Timeline Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            mt: 6,
            px: { xs: 0, md: 4 },
            position: "relative",
          }}
        >
          {[
            {
              year: "2017",
              text: "Founded to deliver innovative car services.",
              icon: "🚗",
            },
            {
              year: "2024",
              text: "Reached 100,000 happy customers.",
              icon: "🎉",
            },
          ].map((milestone, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.1 }}
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", md: "300px" },
                mb: { xs: 4, md: 0 },
                textAlign: "center",
                position: "relative",
                zIndex: 2, // Ensures the milestones are on top of the line
              }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "50%",
                  width: "80px",
                  height: "80px",
                  lineHeight: "80px",
                  margin: "0 auto",
                  boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.1)",
                  fontSize: "2.5rem",
                  color: "orangeRed",
                  mb: 3,
                }}
              >
                {milestone.icon}
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "orangeRed" }}
              >
                {milestone.year}
              </Typography>
              <Typography variant="body2" sx={{ color: "black", mt: 1 }}>
                {milestone.text}
              </Typography>
            </motion.div>
          ))}
        </Box>
      </Box>

      {/* Newsletter Section */}
      {/* <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 8,
          py: 5,
          background: "linear-gradient(135deg, #f7f7f7, #fff)",
          borderRadius: "12px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: "bold", color: "orangeRed", mb: 2 }}
        >
          Stay Updated
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "black", mb: 4, maxWidth: "600px", textAlign: "center" }}
        >
          Subscribe to our newsletter for the latest updates on car services and
          exclusive offers.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "600px",
          }}
        >
          <TextField
            label="Email Address"
            variant="outlined"
            sx={{
              flex: 1,
              mb: { xs: 2, md: 0 },
              mr: { md: 2 },
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
            }}
          />
          <Button
            variant="contained"
            sx={{
              px: 5,
              py: 2,
              backgroundColor: "orangeRed",
              color: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: "orange",
                transform: "scale(1.05)",
                boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            Subscribe
          </Button>
        </Box>
      </Box> */}
    </Container>
  );
};

export default AboutPage;
